// features/workstations/qqq/today/morning-outlook-card.js
// ═══════════════════════════════════════════════════════════
// Morning Outlook Card — Shift's Jarvis 5-point read for today
// ═══════════════════════════════════════════════════════════
//
// Per Tim 2026-04-29: "the 1 thing I would like more than any of
// those is how can shift the information in the morning and give
// me the outlook for that day."
//
// This is the ONE card that synthesizes everything else. The
// other cards (Composite, Vehicle, Calendar, News) are inputs;
// this is the read. Lives at the top of the TODAY tab so it's
// the first thing seen.
//
// 5 sections (Jarvis structure per CLAUDE.md):
//   1. THE READ                - what is the macro saying?
//   2. WHAT TO DO NOW          - TAKE / SKIP / WAIT + specifics
//   3. WHAT TO PREPARE FOR     - afternoon catalysts, regime flips
//   4. WHAT YOU MIGHT BE MISSING - contradictions, blind spots
//   5. WHAT'S NOT SHOWING YET  - emergent patterns, analogs
//
// Generated server-side by /api/qqq/morning-outlook (calls Claude
// Sonnet with composite + news + calendar + vehicle + analog
// sessions + the user-context blob).
//
// Phase 2 additions (2026-04-29):
//   - Cron pre-generation at 9:30 ET means today's outlook is
//     already cached when Tim opens the workstation; the card
//     loads instantly without waiting on Claude.
//   - Rating strip ("did this help?") at the bottom captures
//     Helped / Misleading / Mixed / Neutral feedback for the
//     voice-tuning calibration loop.
//   - History strip shows the last 5 sessions' outlooks +
//     ratings so Tim can scan recent reads.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const SECTIONS = [
  { key: 'the_read',                  label: 'The Read',                 tone: 'gold' },
  { key: 'what_to_do_now',            label: 'What to Do Now',           tone: 'sage' },
  { key: 'what_to_prepare_for',       label: 'What to Prepare For',      tone: 'teal' },
  { key: 'what_you_might_be_missing', label: 'What You Might Be Missing', tone: 'terra' },
  { key: 'whats_not_showing_yet',     label: "What's Not Showing Yet",   tone: 'purple' },
];

const RATING_OPTIONS = [
  { value: 'helped',     label: 'Helped',     tone: 'sage'   },
  { value: 'mixed',      label: 'Mixed',      tone: 'gold'   },
  { value: 'misleading', label: 'Misleading', tone: 'terra'  },
  { value: 'neutral',    label: 'Neutral',    tone: 'steel'  },
];

export default function MorningOutlookCard({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingNote, setRatingNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const buildParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (workstation?.id) p.set('workstation_id', workstation.id);
    if (userId) p.set('user_id', userId);
    Object.entries(extra).forEach(([k, v]) => { if (v != null) p.set(k, String(v)); });
    return p;
  }, [workstation?.id, userId]);

  const load = useCallback(async ({ fresh = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams(fresh ? { fresh: 'true' } : {});
      const url = `/api/qqq/morning-outlook${params.toString() ? '?' + params : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setRatingNote(json?.voice_note || '');
      if (json.error) setError(json.error);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    if (history) return;
    setHistoryLoading(true);
    try {
      const params = buildParams({ history: 'true' });
      const res = await fetch(`/api/qqq/morning-outlook?${params}`, { cache: 'no-store' });
      if (res.ok) {
        const json = await res.json();
        setHistory(json.items || []);
      }
    } catch (_histErr) {
      // history is optional; ignore
    } finally {
      setHistoryLoading(false);
    }
  }, [buildParams, history]);

  const submitRating = async (rating) => {
    if (!data?.id) return;
    setRatingSubmitting(true);
    try {
      const res = await fetch('/api/qqq/morning-outlook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outlook_id: data.id,
          rating,
          note: ratingNote.trim() || undefined,
        }),
      });
      if (res.ok) {
        // Update local state without re-fetch
        setData(prev => ({ ...prev, voice_rating: rating }));
        // If history was loaded, invalidate so it re-fetches with the new rating
        setHistory(null);
      }
    } catch (_rateErr) {
      // surface nothing here — user can retry
    } finally {
      setRatingSubmitting(false);
    }
  };

  const outlook = data?.outlook;
  const inputs = data?.inputs;
  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const sourceLabel = data?.source === 'cron' ? 'cron · 9:30 ET'
    : data?.source === 'manual_refresh' ? 'manual refresh'
    : data?.source || '';
  const currentRating = data?.voice_rating || null;

  return (
    <div className="qqq-outlook-card">
      <div className="qqq-outlook-card__header">
        <div className="qqq-outlook-card__title-block">
          <div className="qqq-outlook-card__eyebrow">Shift Morning Outlook</div>
          <div className="qqq-outlook-card__title">
            {inputs?.event_date || 'Today'}
            {inputs?.composite && (
              <span className="qqq-outlook-card__composite-chip">
                Composite {inputs.composite.score >= 0 ? '+' : ''}{inputs.composite.score} · {inputs.composite.band}
              </span>
            )}
            {inputs?.composite?.is_event_day && (
              <span className="qqq-outlook-card__event-chip">EVENT DAY</span>
            )}
            {inputs?.whisperer_count > 0 && (
              <span className="qqq-outlook-card__whisperer-chip">
                {inputs.whisperer_count} Whisperer {inputs.whisperer_count === 1 ? 'print' : 'prints'}
              </span>
            )}
          </div>
        </div>
        <div className="qqq-outlook-card__header-actions">
          {generatedAt && (
            <span className="qqq-outlook-card__timestamp">
              {sourceLabel ? `${sourceLabel} · ` : ''}as of {fmtTime(generatedAt)}
            </span>
          )}
          <button
            type="button"
            onClick={() => load({ fresh: true })}
            className="qqq-outlook-card__refresh-btn"
            disabled={loading}
          >
            {loading ? 'Generating…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="qqq-outlook-card__collapse-btn"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {loading && !outlook && (
            <div className="qqq-outlook-card__loading">
              Reading composite, news, calendar, and your last 14 sessions…
            </div>
          )}

          {error && !outlook && (
            <div className="qqq-outlook-card__error">
              Outlook generation failed: {error}
            </div>
          )}

          {outlook && (
            <div className="qqq-outlook-card__sections">
              {/* TRADING TRANSLATION — top, most actionable */}
              {outlook.trade_translation && outlook.trade_translation.direction && (
                <TradeTranslationBlock translation={outlook.trade_translation} />
              )}

              {SECTIONS.map(({ key, label, tone }) => {
                const text = outlook[key];
                if (!text) return null;
                return (
                  <div
                    key={key}
                    className={`qqq-outlook-card__section qqq-outlook-card__section--${tone}`}
                  >
                    <div className="qqq-outlook-card__section-label">{label}</div>
                    <div className="qqq-outlook-card__section-text">{text}</div>
                  </div>
                );
              })}

              {/* REASONING CHAIN — collapsible "WHY?" expand */}
              {Array.isArray(outlook.reasoning_chain) && outlook.reasoning_chain.length > 0 && (
                <ReasoningChainBlock entries={outlook.reasoning_chain} />
              )}

              {outlook._parse_error && (
                <div className="qqq-outlook-card__warning">
                  Outlook parser couldn&apos;t structure Claude&apos;s response.
                  Showing raw text in &quot;The Read&quot;. Refresh to retry.
                </div>
              )}
            </div>
          )}

          {/* Inputs strip — what was used to generate this read */}
          {inputs && !loading && (
            <div className="qqq-outlook-card__inputs-strip">
              <span className="qqq-outlook-card__input-tag">
                {inputs.composite ? `Composite ${inputs.composite.score >= 0 ? '+' : ''}${inputs.composite.score}` : 'No composite'}
              </span>
              <span className="qqq-outlook-card__input-tag">
                {inputs.calendar_count} {inputs.calendar_count === 1 ? 'event' : 'events'}
              </span>
              <span className="qqq-outlook-card__input-tag">
                {inputs.news_count} news
              </span>
              <span className="qqq-outlook-card__input-tag">
                {inputs.recent_sessions} analog {inputs.recent_sessions === 1 ? 'session' : 'sessions'}
              </span>
              <span className="qqq-outlook-card__input-tag">
                {inputs.user_context_loaded ? 'personalized ✓' : 'generic'}
              </span>
            </div>
          )}

          {/* Voice rating strip — calibration loop */}
          {outlook && data?.id && (
            <div className="qqq-outlook-card__rating">
              <div className="qqq-outlook-card__rating-prompt">
                After your session — did this read help or mislead?
              </div>
              <div className="qqq-outlook-card__rating-buttons">
                {RATING_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`qqq-outlook-card__rating-btn qqq-outlook-card__rating-btn--${opt.tone}${currentRating === opt.value ? ' is-selected' : ''}`}
                    onClick={() => submitRating(opt.value)}
                    disabled={ratingSubmitting}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                className="qqq-outlook-card__rating-note"
                placeholder="Optional: what was off / on-point about the read?"
                value={ratingNote}
                onChange={e => setRatingNote(e.target.value)}
                onBlur={() => {
                  // Save note silently when the user blurs, only if they've already chosen a rating
                  if (currentRating && data?.id && ratingNote.trim()) {
                    submitRating(currentRating);
                  }
                }}
              />
              {currentRating && (
                <div className="qqq-outlook-card__rating-confirm">
                  Logged as <strong>{currentRating}</strong> · feeds the voice-tuning loop.
                </div>
              )}
            </div>
          )}

          {/* History toggle */}
          {data?.id && (
            <div className="qqq-outlook-card__history">
              <button
                type="button"
                className="qqq-outlook-card__history-toggle"
                onClick={() => {
                  setShowHistory(s => !s);
                  if (!history) loadHistory();
                }}
              >
                {showHistory ? '▾' : '▸'} Past outlooks (last 14 days)
              </button>
              {showHistory && (
                <div className="qqq-outlook-card__history-list">
                  {historyLoading && (
                    <div className="qqq-outlook-card__history-loading">Loading history…</div>
                  )}
                  {!historyLoading && history && history.length === 0 && (
                    <div className="qqq-outlook-card__history-empty">No past outlooks yet.</div>
                  )}
                  {!historyLoading && history && history.length > 0 && history.map(h => (
                    <div key={h.id} className="qqq-outlook-card__history-row">
                      <div className="qqq-outlook-card__history-date">{h.event_date}</div>
                      <div className="qqq-outlook-card__history-snippet">
                        {(h.outlook?.the_read || '').slice(0, 140)}
                        {(h.outlook?.the_read || '').length > 140 ? '…' : ''}
                      </div>
                      {h.voice_rating && (
                        <span className={`qqq-outlook-card__history-rating qqq-outlook-card__history-rating--${ratingTone(h.voice_rating)}`}>
                          {h.voice_rating}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function fmtTime(d) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d) + ' ET';
}

function ratingTone(rating) {
  switch (rating) {
    case 'helped':     return 'sage';
    case 'misleading': return 'terra';
    case 'mixed':      return 'gold';
    case 'neutral':    return 'steel';
    default:           return 'steel';
  }
}

// ═══════════════════════════════════════════════════════════
// Trading Translation block — converts macro into Tim's specific actions
// ═══════════════════════════════════════════════════════════
function TradeTranslationBlock({ translation }) {
  const t = translation || {};
  const directionTone =
    /LONG/i.test(t.direction) ? 'sage' :
    /SHORT/i.test(t.direction) ? 'terra' :
    /BOTH/i.test(t.direction) ? 'gold' :
    'steel';

  return (
    <div className={`qqq-outlook-card__translation qqq-outlook-card__translation--${directionTone}`}>
      <div className="qqq-outlook-card__translation-header">
        TRADING TRANSLATION
      </div>
      <div className="qqq-outlook-card__translation-grid">
        {t.direction && (
          <TranslationRow
            label="Direction"
            value={t.direction}
            reason={t.direction_reason}
            tone={directionTone}
          />
        )}
        {t.vehicle && (
          <TranslationRow label="Vehicle" value={t.vehicle} reason={t.vehicle_reason} tone="teal" />
        )}
        {t.size && (
          <TranslationRow label="Size" value={t.size} reason={t.size_reason} tone="purple" />
        )}
        {t.time_focus && (
          <TranslationRow label="Time" value={t.time_focus} reason={t.time_reason} tone="steel" />
        )}
        {Array.isArray(t.avoid) && t.avoid.length > 0 && (
          <div className="qqq-outlook-card__translation-row">
            <div className="qqq-outlook-card__translation-label">Avoid</div>
            <ul className="qqq-outlook-card__translation-list qqq-outlook-card__translation-list--avoid">
              {t.avoid.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(t.flips_if) && t.flips_if.length > 0 && (
          <div className="qqq-outlook-card__translation-row">
            <div className="qqq-outlook-card__translation-label">Flips if</div>
            <ul className="qqq-outlook-card__translation-list qqq-outlook-card__translation-list--flips">
              {t.flips_if.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function TranslationRow({ label, value, reason, tone }) {
  return (
    <div className={`qqq-outlook-card__translation-row qqq-outlook-card__translation-row--${tone}`}>
      <div className="qqq-outlook-card__translation-label">{label}</div>
      <div className="qqq-outlook-card__translation-value">{value}</div>
      {reason && (
        <div className="qqq-outlook-card__translation-reason">{reason}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Reasoning Chain block — collapsible "WHY?" expand
// ═══════════════════════════════════════════════════════════
function ReasoningChainBlock({ entries }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(entries) || entries.length === 0) return null;

  return (
    <div className="qqq-outlook-card__reasoning">
      <button
        type="button"
        className="qqq-outlook-card__reasoning-toggle"
        onClick={() => setOpen(o => !o)}
      >
        {open ? '▾' : '▸'} Why? ({entries.length} {entries.length === 1 ? 'input' : 'inputs'})
      </button>
      {open && (
        <ul className="qqq-outlook-card__reasoning-list">
          {entries.map((e, i) => (
            <li key={i} className="qqq-outlook-card__reasoning-item">
              <div className="qqq-outlook-card__reasoning-input">{e.input}</div>
              <div className="qqq-outlook-card__reasoning-arrow">→</div>
              <div className="qqq-outlook-card__reasoning-interpretation">{e.interpretation}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
